/**
 * PD-17590 — a phone number formatted while it is typed: "2485551234" reads
 * "(248) 555-1234" as the digits arrive, the way the number is written on the page.
 *
 * Only what the visitor sees changes. The text is still sent as typed and the server
 * stores it in E.164 (`parsePhoneWithExtension`), so this accepts everything that does:
 * - a US or Canadian number, with or without the leading 1 or +1;
 * - an extension after it ("x12", "ext 12", "#12"), kept as typed;
 * - any other country (a "+" not followed by 1), left exactly as typed, since its
 *   grouping differs from country to country.
 *
 * Formatting the result again changes nothing, so a value that is already formatted (or
 * stored as +12485551234) reads the same.
 */
export function formatPhoneAsTyped(raw: string): string {
	const input = raw.replace(/^\s+/, '');
	if (input.startsWith('+') && !/^\+\s*1/.test(input)) {
		return input;
	}

	const plusOne = input.startsWith('+');
	let index = plusOne ? input.indexOf('1') + 1 : 0;
	let digits = '';
	let trunk = false;
	for (; index < input.length && digits.length < 10; index += 1) {
		const character = input.charAt(index);
		if (/\d/.test(character)) {
			// No US area code starts with 1, so a leading 1 is the country code.
			if (!plusOne && !trunk && digits === '' && character === '1') {
				trunk = true;
			} else {
				digits += character;
			}
		} else if (/[a-z#]/i.test(character)) {
			// The start of an extension.
			break;
		}
	}

	const prefix = plusOne ? '+1' : trunk ? '1' : '';
	const national =
		digits.length === 0
			? ''
			: digits.length <= 3
				? `(${digits}`
				: digits.length <= 6
					? `(${digits.slice(0, 3)}) ${digits.slice(3)}`
					: `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
	// Digits past the tenth with nothing before them are not an extension: dropped, as a
	// ten-digit field would.
	const rest = input.slice(index).trim();
	const extension = /[a-z#]/i.test(rest.charAt(0)) ? rest : '';

	return [prefix, national, extension].filter((part) => part !== '').join(' ');
}

/** How many digits come before a position in a phone number's text. */
export function phoneDigitsBefore(text: string, position: number): number {
	return text.slice(0, position).replace(/\D/g, '').length;
}

/**
 * Where the caret goes in the formatted text so the same number of digits stays before
 * it: right after the last of them, or at the start when there are none.
 */
export function phoneCaretAfterDigits(formatted: string, digits: number): number {
	if (digits <= 0) {
		return 0;
	}
	let seen = 0;
	for (let index = 0; index < formatted.length; index += 1) {
		if (/\d/.test(formatted.charAt(index))) {
			seen += 1;
			if (seen === digits) {
				return index + 1;
			}
		}
	}
	return formatted.length;
}

/**
 * The text after a Backspace that only removed formatting ("(248) 555-1234" with the
 * caret after ")"): the digit before the caret goes instead, so the key never does
 * nothing. Null when the Backspace removed a digit itself.
 */
export function phoneBackspaceOverFormatting(
	before: string,
	after: string,
	caret: number,
): { text: string; caret: number } | null {
	if (after.replace(/\D/g, '') !== before.replace(/\D/g, '')) {
		return null;
	}
	for (let index = caret - 1; index >= 0; index -= 1) {
		if (/\d/.test(after.charAt(index))) {
			return {
				text: after.slice(0, index) + after.slice(index + 1),
				caret: index,
			};
		}
	}
	return null;
}
